import { Input, PhoneInput, Checkbox, Button } from '@unified-checkout/design-system';

import type { IDynamicFieldProps } from '@app/dashboard/types/innerLayout/bills/category-services';
import { isAllowedCountry } from '@app/dashboard/utils/isAllowedCountry';
import { COUNTRIES } from '@app/dashboard/constants/countries';

import styles from './dynamic-field.module.scss';

export const DynamicField = ({ field, value, error, onChange, disabled }: IDynamicFieldProps) => {
  if (field.is_hidden) return null;

  const stringifiedValue = (typeof value === 'string' || typeof value === 'number') ? String(value) : '';

  const handleSlabClick = (slab: number) => {
    onChange(field.field_name, String(slab), field);
  };

  const renderSlabs = () => {
    if (!field.available_slabs?.length) return null;

    return (
      <div className={styles['dynamic-field__slabs']}>
        {field.available_slabs.map((slab) => (
          <Button
            key={`${field.field_name}-${slab}`}
            variant='outlined'
            color='secondary'
            size='sm'
            text={String(slab)}
            onClickHandler={() => !disabled && handleSlabClick(slab)}
          />
        ))}
      </div>
    );
  };

  switch (field.field_type) {
    case 'phone_number':
    case 'mobile_number':
      return (
        <PhoneInput
          id={field.field_name}
          name={field.field_name}
          label={field.display_title}
          placeholder={field.placeholder}
          phoneNumber={stringifiedValue}
          onChangeHandler={(val: string) => onChange(field.field_name, val, field)}
          error={error}
          disabled={disabled}
        />
      );

    case 'landline':
      return (
        <Input
          name={field.field_name}
          label={field.display_title}
          type='number'
          value={stringifiedValue}
          placeholder={field.placeholder}
          status={error ? 'error' : 'default'}
          error={error}
          disabled={disabled}
          onChange={(e: any) => onChange(field.field_name, e.target.value, field)}
        />
      );

    case 'amount':
      return (
        <div className={styles['dynamic-field__amount']}>
          <Input
            name={field.field_name}
            label={field.display_title}
            type='number'
            value={stringifiedValue}
            placeholder={field.placeholder}
            status={error ? 'error' : 'default'}
            error={error}
            leadingText={isAllowedCountry(COUNTRIES.EGY) ? 'EGP' : undefined}
            disabled={disabled}
            onChange={(e: any) => onChange(field.field_name, e.target.value, field)}
          />
          {renderSlabs()}
        </div>
      );

    case 'date':
      return (
        <Input
          name={field.field_name}
          label={field.display_title}
          // type='date'
          value={stringifiedValue}
          placeholder={field.placeholder}
          status={error ? 'error' : 'default'}
          error={error}
          disabled={disabled}
          onChange={(e: any) => onChange(field.field_name, e.target.value, field)}
        />
      );

    case 'email':
      return (
        <Input
          name={field.field_name}
          label={field.display_title}
          type='email'
          value={stringifiedValue}
          placeholder={field.placeholder}
          status={error ? 'error' : 'default'}
          error={error}
          disabled={disabled}
          onChange={(e: any) => onChange(field.field_name, e.target.value, field)}
        />
      );

    case 'Boolean':
      return (
        <Checkbox
          id={field.field_name}
          type='checkbox'
          text={field.display_title}
          checked={value === 'true' || value === true}
          disabled={disabled}
          onClickHandler={(checked: boolean) => onChange(field.field_name, String(checked), field)}
        />
      );

    case 'text':
    case 'bold_text':
    case 'national_id':
    case 'bill_reference':
    case 'customer_code':
    case 'account_number':
    case 'customer_id':
    case 'service_code':
    case 'reading':
    case 'voucher_id':
    case 'meter_number':
    case 'card_number':
    case 'subscription_number':
    case 'amenli':
    default:
      return (
        <Input
          name={field.field_name}
          label={field.display_title}
          value={stringifiedValue}
          placeholder={field.placeholder}
          status={error ? 'error' : 'default'}
          error={error}
          disabled={disabled}
          onChange={(e: any) => onChange(field.field_name, e.target.value, field)}
        />
      );
  }
};
